import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Radio, Alert, Chip } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Modal from '../../../../common/Modal';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import { employmentApi } from '../../../../../services/api';
import { COLORS } from '../../../../../constants/radar';
import { formatTimeDuration } from '../../../../../utils/dateUtils';
import { contractIdentityLabel } from '../../../../../constants/contractActions';
import {
  HIRE_DATE_SOURCE_KIND as KIND,
  findOpenEpisode,
  formatEpisodeDate,
  formatEpisodeRange,
  isHireDateSource,
  toEpisodeDayKey,
} from '../../../../../constants/employmentEpisodes';

dayjs.extend(utc);

/**
 * ADR-138 — pick the employment episode that supplies an employee's hire date.
 *
 * Purely presentational about the OUTCOME: it fetches the episode list, renders the
 * choices and the two consequences, and hands the parent `{ kind, episodeId, date }`.
 * The parent decides what that means — the rehire form keeps it in form state and
 * sends it on the REHIRE event, the Employment Information tab PUTs it immediately.
 * One component so the two surfaces cannot describe the same choice differently.
 *
 * Selection kinds live in constants/employmentEpisodes.js as HIRE_DATE_SOURCE_KIND.
 */

const SelectableRow = ({ selected, onClick, primary, secondary, chips, disabled }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      p: 1.5,
      mb: 1,
      border: '1px solid',
      borderColor: selected ? COLORS.PRIMARY : '#e2e8f0',
      backgroundColor: selected ? '#f8fafc' : '#ffffff',
      borderRadius: '6px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      '&:hover': disabled ? {} : { borderColor: COLORS.PRIMARY },
    }}
  >
    <Radio
      checked={selected}
      onChange={disabled ? undefined : onClick}
      disabled={disabled}
      size="small"
      sx={{ p: 0, mt: '2px' }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          {primary}
        </Typography>
        {(chips || []).map((chip) => (
          <Chip
            key={chip}
            label={chip}
            size="small"
            sx={{ backgroundColor: '#e0e7ff', color: '#4f46e5', fontWeight: 500, fontSize: '11px' }}
          />
        ))}
      </Box>
      {secondary && (
        <Typography
          variant="body2"
          sx={{ color: '#64748B', fontSize: '13px', mt: 0.5, whiteSpace: 'pre-wrap' }}
        >
          {secondary}
        </Typography>
      )}
    </Box>
  </Box>
);

const EpisodePicker = ({
  open,
  onClose,
  onConfirm,
  employeeId,
  currentHireDate,
  currentTenure,
  newContractStartDate,
  allowKeepCurrent,
  initialSelection,
  title,
  confirmText,
  isSubmitting,
}) => {
  const [episodes, setEpisodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selection, setSelection] = useState(initialSelection || null);

  useEffect(() => {
    if (!open || !employeeId) return;

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        // ADR-138 §7.1: no new read endpoint. This one already returns the episode
        // `payload`, so the ADR-137 contract number comes along for free.
        const data = await employmentApi.getEmploymentEpisodes(employeeId);
        if (!cancelled) setEpisodes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('EpisodePicker: failed to load employment episodes', err);
        if (!cancelled) setLoadError('Failed to load employment episodes.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [open, employeeId]);

  useEffect(() => {
    if (open) setSelection(initialSelection || null);
    // Re-seeding only when the modal opens is deliberate: the parent's default can
    // move while the modal is open (the rehire contract start date is editable
    // behind it) and yanking the user's chosen row out from under them is worse
    // than a stale default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The date the current selection resolves to, or null when nothing is chosen.
  const selectedDate = useMemo(() => {
    if (!selection) return null;
    if (selection.kind === KIND.KEEP) return toEpisodeDayKey(currentHireDate);
    if (selection.kind === KIND.NEW) return newContractStartDate || null;
    const episode = episodes.find((e) => e.id === selection.episodeId);
    return episode ? toEpisodeDayKey(episode.startDate) : null;
  }, [selection, episodes, currentHireDate, newContractStartDate]);

  // What the person's contract actually starts on — the rehire being entered, or
  // failing that the still-open episode. Used only for the status warning.
  const contractStartDate = useMemo(() => {
    if (newContractStartDate) return newContractStartDate;
    const openEpisode = findOpenEpisode(episodes);
    return openEpisode ? toEpisodeDayKey(openEpisode.startDate) : null;
  }, [newContractStartDate, episodes]);

  const currentHireDayKey = toEpisodeDayKey(currentHireDate);
  const hireDateChanges = Boolean(selectedDate) && selectedDate !== currentHireDayKey;

  // ADR-138 decision 5, made visible at the moment of choosing. Tenure derives from
  // hireDate alone, so any re-point restarts it — including on a veteran.
  const tenureWarning = hireDateChanges
    ? `Time in organization will be recalculated from ${formatEpisodeDate(selectedDate)}${
      currentTenure ? ` — it currently reads ${formatTimeDuration(currentTenure)}.` : '.'
    }`
    : null;

  // ADR-138 §8.2: a past hire date with a future contract start is exactly the
  // reading this ADR exists to fix. It stays reachable, because it is the correct
  // answer for a contiguous renewal — so warn and obey rather than forbid.
  const today = dayjs.utc().format('YYYY-MM-DD');
  const statusWarning =
    selectedDate && contractStartDate && selectedDate < today && contractStartDate > today
      ? `This person will read as "employed" from today, although their contract starts on ${formatEpisodeDate(contractStartDate)}.`
      : null;

  const handleConfirm = () => {
    if (!selection) return;
    onConfirm({
      kind: selection.kind,
      episodeId: selection.kind === KIND.EPISODE ? selection.episodeId : null,
      date: selectedDate,
    });
  };

  const isSelected = (kind, episodeId) =>
    Boolean(selection) && selection.kind === kind &&
    (kind !== KIND.EPISODE || selection.episodeId === episodeId);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      onSubmit={handleConfirm}
      title={title}
      submitText={confirmText}
      submitDisabled={!selection || !selectedDate || isSubmitting || isLoading}
      maxWidth="640px"
      width="640px"
    >
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 0.5 }}>
        <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
          The hire date is taken from an employment episode, so it always matches a real
          period of employment. Current hire date:{' '}
          <strong>{currentHireDayKey ? formatEpisodeDate(currentHireDayKey) : 'not set'}</strong>
          {currentHireDayKey && !isLoading && !episodes.some((e) => isHireDateSource(e, currentHireDate)) && (
            <em> — it does not match any episode start date.</em>
          )}
        </Typography>

        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {allowKeepCurrent && currentHireDayKey && (
              <SelectableRow
                selected={isSelected(KIND.KEEP)}
                onClick={() => setSelection({ kind: KIND.KEEP })}
                primary={`Keep the current hire date — ${formatEpisodeDate(currentHireDayKey)}`}
                secondary="Right for a contiguous renewal: the person never actually left, so their tenure should not restart."
              />
            )}

            {newContractStartDate && (
              <SelectableRow
                selected={isSelected(KIND.NEW)}
                onClick={() => setSelection({ kind: KIND.NEW })}
                primary={`Use this rehire's start date — ${formatEpisodeDate(newContractStartDate)}`}
                secondary="Right for a return after a real break: employment starts again on this date."
                chips={['new contract']}
              />
            )}

            {episodes.length === 0 && !loadError && (
              <Typography variant="body2" sx={{ color: '#64748B', fontStyle: 'italic' }}>
                This person has no employment episodes.
              </Typography>
            )}

            {episodes.map((episode, index) => {
              // ADR-156 §6 / ADR-138 §12 — the row carries the contract's
              // IDENTITY (number · type · entity), not just a date range: once
              // two contracts can be open at once, two rows may share a start
              // date and the range alone cannot tell them apart. The label
              // reads the episode's OWN terms (ADR-148 §3.2) — the employee
              // is deliberately not passed, so a historical episode is never
              // labelled with today's type and entity.
              const identity = contractIdentityLabel(null, episode, { withStartDate: false });
              const chips = [];
              if (identity) chips.push(identity);
              if (isHireDateSource(episode, currentHireDate)) chips.push('current source');

              return (
                <SelectableRow
                  key={episode.id}
                  selected={isSelected(KIND.EPISODE, episode.id)}
                  onClick={() => setSelection({ kind: KIND.EPISODE, episodeId: episode.id })}
                  primary={`Episode ${episodes.length - index} — ${formatEpisodeRange(episode)}`}
                  secondary={episode.comments || null}
                  chips={chips}
                />
              );
            })}

            {tenureWarning && <Alert severity="info" sx={{ mt: 2 }}>{tenureWarning}</Alert>}
            {statusWarning && <Alert severity="warning" sx={{ mt: 2 }}>{statusWarning}</Alert>}
          </>
        )}
      </Box>
    </Modal>
  );
};

EpisodePicker.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  employeeId: PropTypes.string,
  /** The employee's hire date as stored today. */
  currentHireDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** `{ years, months, days }` from the employee payload, for the tenure sentence. */
  currentTenure: PropTypes.object,
  /** Rehire only: 'YYYY-MM-DD' contract start being entered, offered as a choice. */
  newContractStartDate: PropTypes.string,
  /** Rehire only: offer "keep the current hire date" as an explicit option. */
  allowKeepCurrent: PropTypes.bool,
  initialSelection: PropTypes.object,
  title: PropTypes.string,
  confirmText: PropTypes.string,
  isSubmitting: PropTypes.bool,
};

EpisodePicker.defaultProps = {
  allowKeepCurrent: false,
  title: 'Hire date source',
  confirmText: 'Apply',
  isSubmitting: false,
};

export default EpisodePicker;
